// scheduling/hooks/useMe.js
// Single source of truth for resolving the logged-in user → their rprs StaffId.
//
// This three-step resolver was previously copy-pasted (and DRIFTED) across
// UnifiedDashboard, ManagerDashboard, and StaffDashboard — only ManagerDashboard
// carried the masked-email fallback, so a masked SWA session ("tma*****@bmss.com")
// silently failed to resolve "me" in the badge counts + the staff auto-select.
// Unifying on the 3-step version fixes that latent bug everywhere at once.

import { useMemo } from 'react';
import { useAuth } from '../../auth/AuthContext';

/**
 * Resolve a staff row for the given email/username against a staff list.
 * 1. exact email match
 * 2. username-prefix match (local-dev / prefix fallback)
 * 3. masked-email single-candidate fallback (SWA sometimes masks userDetails)
 * Returns the staff object, or null.
 */
export function resolveMe(staffList, email, username) {
  const sList = Array.isArray(staffList) ? staffList : [];
  let me = email && sList.find(s => (s.Email || '').toLowerCase() === email.toLowerCase());
  if (!me) {
    const prefix = (email || username || '').split('@')[0].toLowerCase();
    if (prefix) me = sList.find(s => (s.Email || '').toLowerCase().startsWith(prefix + '@'));
  }
  if (!me && (email || '').includes('*')) {
    // SWA masked the email (e.g. "tma*****@bmss.com"). Resolve by the unmasked
    // prefix — only when exactly one staff matches, so ambiguous cases fail safely.
    const unmaskedPrefix = email.split('@')[0].replace(/\*+/g, '').toLowerCase();
    if (unmaskedPrefix.length >= 2) {
      const candidates = sList.filter(s => (s.Email || '').toLowerCase().startsWith(unmaskedPrefix));
      if (candidates.length === 1) me = candidates[0];
    }
  }
  return me || null;
}

/**
 * Hook wrapper — resolves "me" from the current auth context against `staffList`.
 * Pure derivation (no fetch); the owner fetches staff once and passes it in.
 * Returns { me, meStaffId, meName }.
 */
export function useMe(staffList) {
  const { email, username } = useAuth();
  return useMemo(() => {
    const me = resolveMe(staffList, email, username);
    return {
      me,
      meStaffId: me ? me.StaffId : null,
      meName: me ? (me.Name || me.StaffName || null) : null,
    };
  }, [staffList, email, username]);
}
